import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SelectField } from './select-field';

export interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  /** Plural noun of the counter, e.g. « demandes ». */
  itemLabel: string;
  onPageChange: (page: number) => void;
  ariaLabel: string;
  /** Appended to the counter, e.g. « triées par ancienneté » (07). */
  suffix?: string;
  /** « Par page » selector (07). */
  pageSizeOptions?: readonly number[];
  onPageSizeChange?: (pageSize: number) => void;
}

const pageButton = 'flex h-10 min-w-10 items-center justify-center rounded-control border font-[inherit] text-sm';

export function Pagination({ page, pageSize, total, itemLabel, onPageChange, ariaLabel, suffix, pageSizeOptions, onPageSizeChange }: PaginationProps) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);
  return (
    <nav aria-label={ariaLabel} className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[13px] text-muted-foreground">
          {first}–{last} sur {total} {itemLabel}
          {suffix ? ` · ${suffix}` : ''}
        </span>
        {pageSizeOptions && onPageSizeChange && (
          <SelectField label="Par page" size="sm" value={String(pageSize)} onChange={(event) => onPageSizeChange(Number(event.target.value))} className="min-h-9">
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </SelectField>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Page précédente"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className={cn(pageButton, 'w-10 border-border bg-card text-foreground disabled:text-muted-foreground', page > 1 && 'cursor-pointer')}
        >
          <ChevronLeft aria-hidden="true" className="size-4" />
        </button>
        {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            type="button"
            aria-current={n === page ? 'page' : undefined}
            onClick={() => onPageChange(n)}
            className={cn(
              pageButton,
              'px-2',
              n === page ? 'border-primary bg-primary font-bold text-primary-foreground' : 'cursor-pointer border-border bg-card text-foreground',
            )}
          >
            {n}
          </button>
        ))}
        <button
          type="button"
          aria-label="Page suivante"
          disabled={page >= pageCount}
          onClick={() => onPageChange(page + 1)}
          className={cn(pageButton, 'w-10 border-border bg-card text-foreground disabled:text-muted-foreground', page < pageCount && 'cursor-pointer')}
        >
          <ChevronRight aria-hidden="true" className="size-4" />
        </button>
      </div>
    </nav>
  );
}
